import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Store, Crown, Gem, Check, Minus, Sparkle, ArrowRight, Loader2 } from "lucide-react";

// The Estate's three Membership tiers — Boutique (pay-per-card baseline), Goliath and Royal
// (real, live monthly Stripe subscriptions). Feature lists reflect exactly what the backend
// actually grants on activation (server.py /subscription/tier-checkout + tier-verify) — never
// advertised capabilities the code doesn't deliver.
const TIERS = [
  {
    id: "boutique", name: "Boutique", icon: Store, price: "$9.99", cadence: "per card",
    tagline: "Pay-per-card. No commitment, no subscription.",
    features: ["Pay only for what you send — $9.99/card", "Silver Seal courtesy sends + the Curated Gift Wall", "Digital delivery always free", "Upgrade to Goliath or Royal anytime"],
    cta: "Start free", accent: "from-secondary to-primary",
  },
  {
    id: "goliath", name: "Goliath", icon: Gem, price: "$49", cadence: "/ mo", highlight: true,
    tagline: "For the frequent sender — premium unlocked, credits included.",
    features: ["10 Mailing Credits included at signup", "AI Magic Wand + Wishlist Highlights, unlocked permanently", "Legacy Vault, Global Cash Gifting & the Gifted Circle", "Royal Voice (Sovereign Seal) + Bespoke Mashup + Arrangements"],
    cta: "Go Goliath", accent: "from-primary to-secondary",
  },
  {
    id: "royal", name: "Royal", icon: Crown, price: "$99", cadence: "/ mo",
    tagline: "The Sovereign tier — everything, plus Royal-only privileges.",
    features: ["25 Mailing Credits included at signup", "Everything in Goliath, permanently unlocked", "3 Royal Guest Passes every month", "Priority access to Royal Maison Arrangements vendors"],
    cta: "Go Royal", accent: "from-[#c9a24a] to-[#8a6a2a]",
  },
];

const Membership = () => {
  const navigate = useNavigate();
  const [ctaLoading, setCtaLoading] = useState("");
  const [error, setError] = useState("");

  const handleCta = async (id) => {
    if (id === "boutique") { navigate("/cards"); return; }
    setCtaLoading(id); setError("");
    try {
      const res = await api.post("/subscription/tier-checkout", { tier: id });
      if (res.data.mode === "live" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      navigate("/subscription");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
    } finally {
      setCtaLoading("");
    }
  };

  return (
    <AppShell>
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm mb-4">
          <Sparkle className="w-4 h-4 gold-text" /> The Estate
        </div>
        <h1 className="font-serif text-5xl mb-3 shimmer-gold">Choose your Membership</h1>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto">Boutique, Goliath, or Royal — thoughtfulness, elevated.</p>
      </div>

      {error && <div className="max-w-md mx-auto mb-6 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-center" data-testid="membership-error">{error}</div>}

      <div className="grid md:grid-cols-3 gap-6 items-stretch mb-14" data-testid="membership-tiers">
        {TIERS.map((t) => (
          <div key={t.id} data-testid={`tier-${t.id}`}
            className={`surface rounded-[2rem] p-8 flex flex-col card-soft ${t.highlight ? "ring-2 ring-primary/40 relative" : ""}`}>
            {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-white">Most Popular</div>}
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.accent} flex items-center justify-center mb-5`}>
              <t.icon className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-serif text-2xl">{t.name}</h2>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-serif text-4xl">{t.price}</span>
              <span className="text-muted-foreground text-sm">{t.cadence}</span>
            </div>
            <p className="text-muted-foreground text-sm mb-5">{t.tagline}</p>
            <ul className="space-y-3 mb-8 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <div className="w-5 h-5 rounded-full bg-secondary/15 border border-secondary/30 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-secondary" /></div>
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>
            <button data-testid={`tier-cta-${t.id}`} onClick={() => handleCta(t.id)} disabled={ctaLoading === t.id}
              className={`w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-white bg-gradient-to-r ${t.accent} hover:opacity-90 disabled:opacity-60`}>
              {ctaLoading === t.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>{t.cta} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        ))}
      </div>

      {/* Comparison matrix */}
      <div className="surface rounded-[2rem] p-6 overflow-x-auto" data-testid="membership-matrix">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 font-medium text-muted-foreground">Feature</th>
              <th className="py-3 font-medium">Boutique</th>
              <th className="py-3 font-medium">Goliath</th>
              <th className="py-3 font-medium">Royal</th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: "Personal Boutique & Vibe Feed", boutique: true, goliath: true, royal: true },
              { label: "Silver Seal — Courtesy Ledger sends", boutique: true, goliath: true, royal: true },
              { label: "Curated Gift Wall", boutique: true, goliath: true, royal: true },
              { label: "Gold Seal for Loved Ones", boutique: false, goliath: true, royal: true },
              { label: "AI Magic Wand + Wishlist Highlights", boutique: false, goliath: true, royal: true },
              { label: "Global Cash Gifting + Gifted Circle", boutique: false, goliath: true, royal: true },
              { label: "Royal Voice + Bespoke Mashup + Arrangements", boutique: false, goliath: true, royal: true },
              { label: "Monthly Mailing Credits included", boutique: false, goliath: true, royal: true },
              { label: "Monthly Royal Guest Passes (x3)", boutique: false, goliath: false, royal: true },
              { label: "Priority Royal Arrangements vendors", boutique: false, goliath: false, royal: true },
            ].map((row) => (
              <tr key={row.label} className="border-b border-border/50">
                <td className="py-3 text-foreground/90">{row.label}</td>
                <td className="py-3 text-center">{row.boutique ? <Check className="w-4 h-4 text-secondary mx-auto" /> : <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />}</td>
                <td className="py-3 text-center">{row.goliath ? <Check className="w-4 h-4 text-secondary mx-auto" /> : <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />}</td>
                <td className="py-3 text-center">{row.royal ? <Check className="w-4 h-4 text-secondary mx-auto" /> : <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-center text-xs text-muted-foreground/70 mt-6">Goliath &amp; Royal are billed monthly via Stripe · cancel anytime. A one-time Credit Pack is also available on Your Gifted Tier.</p>
    </AppShell>
  );
};

export default Membership;
